import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Button, Divider, message, Modal, Space } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type WhatsappLiveChatModuleSpecsFragment } from '../../../../api/fragments/WhatsappLiveChatModuleSpecs';
import {
    DeleteWhatsappLiveChatSettingsDocument,
    type DeleteWhatsappLiveChatSettingsMutation,
    type DeleteWhatsappLiveChatSettingsMutationVariables,
} from '../../../../api/mutations/deleteWhatsappLiveChatSettings';

export type WhatsappLiveChatSettingActionsProps = {
    module: WhatsappLiveChatModuleSpecsFragment;
    settings: WhatsappLiveChatModuleSpecsFragment['liveChatSettings'][number];
};

const WhatsappLiveChatSettingActions = ({ module, settings }: WhatsappLiveChatSettingActionsProps) => {
    const moduleId = module.id;
    const settingsId = settings.id;

    const apolloClient = useApolloClient();
    const navigate = useNavigate();
    const { t } = useTranslation(['whatsappLiveChatModuleDetails']);

    const deleteAction = useCallback(() => {
        Modal.confirm({
            title: t('whatsappLiveChatModuleDetails:settings.deleteModal.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('whatsappLiveChatModuleDetails:settings.deleteModal.content'),
            okText: t('whatsappLiveChatModuleDetails:settings.deleteModal.submitButton'),
            okType: 'danger',
            cancelText: t('whatsappLiveChatModuleDetails:settings.deleteModal.cancelButton'),
            onOk: async () => {
                message.loading({
                    content: t('whatsappLiveChatModuleDetails:settings.deleteModal.messages.submitting'),
                    key: 'primary',
                    duration: 0,
                });

                await apolloClient
                    .mutate<DeleteWhatsappLiveChatSettingsMutation, DeleteWhatsappLiveChatSettingsMutationVariables>({
                        mutation: DeleteWhatsappLiveChatSettingsDocument,
                        variables: { moduleId, id: settingsId },
                    })
                    .finally(() => {
                        message.destroy('primary');
                    });

                message.success({
                    content: t('whatsappLiveChatModuleDetails:settings.deleteModal.messages.success'),
                    key: 'primary',
                });
            },
        });
    }, [apolloClient, moduleId, settingsId, t]);

    return (
        <Space size="small" split={<Divider type="vertical" />}>
            <Button
                onClick={() => navigate(`/admin/system/modules/${moduleId}/whatsapp/${settingsId}`)}
                size="small"
                style={{ padding: 0 }}
                type="link"
            >
                {t('whatsappLiveChatModuleDetails:settingsTable.actions.update')}
            </Button>
            <Button onClick={deleteAction} size="small" style={{ padding: 0 }} type="link">
                {t('whatsappLiveChatModuleDetails:settingsTable.actions.delete')}
            </Button>
        </Space>
    );
};

export default WhatsappLiveChatSettingActions;
